import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { SrIcon } from "@/components/ui/SrIcon";
import { EquipmentFlagGate } from "@/components/equipment/EquipmentFlagGate";
import { PackingList } from "@/components/equipment/PackingList";
import { useEquipmentAccess, useEquipmentList } from "@/hooks/use-equipment";
import { useProjectEquipment } from "@/hooks/use-equipment-bookings";
import { useProjectForPrint } from "@/hooks/use-project-for-print";
import { formatDayRange } from "@/lib/equipment/availability";
import { packingDepartments, packingEntries, packingPeriod } from "@/lib/equipment/packing-list";

/**
 * /prosjekter/:id/pakkeliste (VIB-2920, mockup H): booket utstyr med deler og
 * tilbehør, laget for mobil og utskrift. Rendres uten AppLayout, så lenken kan
 * deles og skrives ut rent. Avkrysningen lagres ikke.
 */
export default function PackingListPage() {
  return (
    <EquipmentFlagGate>
      <PackingListContent />
    </EquipmentFlagGate>
  );
}

function PackingListContent() {
  const { id } = useParams();
  const access = useEquipmentAccess();
  const { data: project, isLoading: projectLoading, error: projectError } = useProjectForPrint(id);
  const { data: lines = [], isLoading: linesLoading, error: linesError } = useProjectEquipment(id);
  const { data: items = [] } = useEquipmentList(access.canManage, !access.isLoading);

  const partsByItem = useMemo(() => new Map(items.map((i) => [i.id, i.parts])), [items]);
  const entries = useMemo(() => packingEntries(lines, partsByItem), [lines, partsByItem]);
  const period = packingPeriod(lines);
  const departments = packingDepartments(lines);

  const share = async () => {
    const url = window.location.href;
    const title = `Pakkeliste ${project?.project_code ?? ""} ${project?.name ?? ""}`.trim();
    try {
      if (navigator.share) {
        await navigator.share({ title, url });
        return;
      }
      await navigator.clipboard.writeText(url);
      toast.success("Lenken er kopiert");
    } catch (e) {
      if ((e as Error).name !== "AbortError") toast.error(`Kunne ikke dele lenken: ${(e as Error).message}`);
    }
  };

  if (projectLoading || linesLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <SrIcon name="loader2" className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (projectError || !project) {
    return <p className="p-6 text-destructive">Fant ikke prosjektet.</p>;
  }

  return (
    <div className="print-root mx-auto max-w-xl space-y-5 px-4 py-6">
      <div className="print-hide flex items-center gap-2">
        <Button variant="ghost" size="icon" asChild>
          <Link to={`/prosjekter/${project.id}/utstyr`} aria-label="Tilbake til prosjektet">
            <SrIcon name="chevron-left" className="h-5 w-5" />
          </Link>
        </Button>
        <span className="font-display font-semibold">Pakkeliste</span>
        <span className="flex-1" />
        <Button variant="ghost" size="icon" aria-label="Skriv ut" onClick={() => window.print()}>
          <SrIcon name="printer" className="h-5 w-5" />
        </Button>
      </div>
      <div>
        <span className="font-mono text-sm text-muted-foreground">{project.project_code}</span>
        <h1 className="font-display text-2xl font-semibold tracking-[-0.01em]">{project.name}</h1>
        {period && (
          <p className="text-sm text-muted-foreground">
            {formatDayRange(period.from, period.to)}
            {departments.length > 0 ? ` · ${departments.join(", ")}` : ""}
          </p>
        )}
      </div>
      {linesError ? (
        <p className="text-destructive">Feil ved lasting av utstyr: {(linesError as Error).message}</p>
      ) : entries.length === 0 ? (
        <p className="text-sm text-muted-foreground">Ingen utstyr er booket på prosjektet.</p>
      ) : (
        <PackingList entries={entries} />
      )}
      <p className="print-hide text-xs text-muted-foreground">Avkrysningen lagres ikke.</p>
      <Button variant="outline" className="print-hide w-full" onClick={share}>
        <SrIcon name="external-link" className="h-4 w-4" />
        Del lenke
      </Button>
    </div>
  );
}
